// src/utils/errorHandling.ts

import { renderErrorMessage } from "../components/common/toastMessage"; // Assuming toast moved here

/**
 * Checks if an error originates from a browser extension.
 *
 * @param {any} error - The error object to check. It may contain a `stack` property.
 * @param {string} [source] - The source URL or string associated with the error.
 * @returns {boolean} - Returns `true` if the error is determined to originate from a browser extension, otherwise `false`.
 */
function isExtensionError(error: any, source?: string): boolean {
	// Check if error comes from extension URL
	if (source && source.includes("chrome-extension://")) {
		return true;
	}

	// Check error stack for extension URLs
	if (error?.stack && error.stack.includes("chrome-extension://")) {
		return true;
	}

	// Check for other extension patterns
	if (
		error?.stack &&
		(error.stack.includes("moz-extension://") ||
			error.stack.includes("safari-extension://") ||
			error.stack.includes("webkit-masked-url://"))
	) {
		return true;
	}

	return false;
}

/**
 * Global error handler for synchronous errors.
 * Logs the error and displays a generic message to the user.
 */
function handleGlobalError(
	message: Event | string,
	source?: string,
	lineno?: number,
	colno?: number,
	error?: Error
): void {
	// 💡 **SOLUTION:** Ignore generic "Script error" from third-party scripts
	if (message === "Script error." && !source) {
		console.warn("Ignoring generic cross-origin script error.");
		return;
	}

	// Filter out extension errors
	if (isExtensionError(error, source)) {
		console.warn("Browser extension error detected and ignored:", error);
		return;
	}

	console.error(
		"Global Error Caught:",
		message,
		"at",
		source || "N/A",
		"line",
		lineno || "N/A",
		"column",
		colno || "N/A",
		"Error object:",
		error || "N/A"
	);

	// Use the more specific renderErrorMessage function if available
	renderErrorMessage(); // Render generic message

	// Optionally send error to a logging service like Sentry
	// if (typeof Sentry !== 'undefined') {
	//    Sentry.captureException(error || new Error(String(message)));
	// }
}

/**
 * Global handler for unhandled promise rejections.
 * Logs the reason and displays a generic message to the user.
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
	// Filter out extension errors
	if (isExtensionError(event.reason, event.reason?.stack)) {
		console.warn(
			"Browser extension promise rejection detected and ignored:",
			event.reason
		);
		event.preventDefault(); // Prevent the error from appearing in console
		return;
	}

	console.error("Global Unhandled Promise Rejection:", event.reason);

	renderErrorMessage(); // Render generic message

	// Optionally send error to a logging service
	// if (typeof Sentry !== 'undefined') {
	//    Sentry.captureException(event.reason);
	// }
}

/**
 * Sets up global error handlers.
 */
export function setupGlobalErrorHandlers(): void {
	window.onerror = handleGlobalError;
	window.addEventListener("unhandledrejection", handleUnhandledRejection);
}
